import React from "react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  InputKeyLimiter,
  InputPasteLimiter,
} from "@/utils/helper-functions/form.helper";

interface AlertDialogWithTextAreaProps {
  title?: string;
  subtitle?: string;
  onConfirm: (value: string) => void;
  isOpen: boolean;
  toggleDialog: () => void;
  isRequired?: boolean;
  confirmBtnLabel?: string;
  label: string;
  placeholder?: string;
  fieldVariant?: "input" | "textarea";
  fieldType?: React.HTMLInputTypeAttribute | undefined;
  maxChar?: number;
}

export const AlertDialogWithTextArea: React.FC<
  AlertDialogWithTextAreaProps
> = ({
  title = "Are You Sure?",
  subtitle = "Are you sure you want to proceed?",
  onConfirm,
  isOpen,
  toggleDialog,
  isRequired = false,
  confirmBtnLabel = "Yes, Confirm",
  label,
  placeholder = "Enter your message..",
  fieldVariant = "textarea",
  fieldType = "text",
  maxChar,
}: AlertDialogWithTextAreaProps) => {
  const [value, setValue] = React.useState("");
  const [error, setError] = React.useState(false);

  const handleConfirm = (): void => {
    if (isRequired && !value) {
      setError(true);

      return;
    }
    onConfirm(value);
  };

  const renderInputField = (): JSX.Element => {
    if (fieldVariant === "input") {
      return (
        <Input
          value={value}
          type={fieldType}
          onChange={(e) => {
            setValue(e.target?.value);
            if (error) {
              if (isRequired && !e.target?.value) {
                setError(true);
              } else {
                setError(false);
              }
            }
          }}
          onKeyDown={(e) =>
            InputKeyLimiter({
              event: e,
              maxValue: Infinity,
              maxChars: maxChar,
            })
          }
          onPaste={(e) =>
            InputPasteLimiter({
              event: e,
              maxValue: Infinity,
            })
          }
          placeholder={placeholder}
        />
      );
    }

    return (
      <Textarea
        value={value}
        onChange={(e) => {
          setValue(e.target?.value);
          if (error) {
            if (isRequired && !value) {
              setError(true);
            } else {
              setError(false);
            }
          }
        }}
        placeholder={placeholder}
      />
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={toggleDialog}>
      <DialogContent className=''>
        <div className='flex flex-col gap-2 py-4 mb-2'>
          <h1 className='text-2xl font-medium'>{title}</h1>
          <p className='text-sm font-normal text-[#8B8B8B]'>{subtitle}</p>
        </div>
        <section className='space-y-2'>
          <Label required={isRequired} className='font-normal'>
            {label}
          </Label>
          {renderInputField()}
          <span>
            {error && isRequired && (
              <p className='mt-2 text-xs text-red-500'>Required</p>
            )}
          </span>
        </section>
        <div className='flex justify-between mt-4'>
          <Button onClick={toggleDialog} variant={"ghost"}>
            No, Cancel
          </Button>
          <Button onClick={handleConfirm} className='w-[200px]'>
            {confirmBtnLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
